/**
 * DeliveryFormDialog — bám đúng Access spec `frmFactoryQryDeliveryAddStore`
 * (`plan/access_all/form_frmFactoryQryDeliveryAddStore.txt`).
 *
 * Save payload (Access `strU_mainsub` / `strI_mainsub`, exact order):
 *   gdn_id, date_out, do_no, from_warehouse, to_warehouse, lot_id,
 *   lot_name_buyer, stack_id, package, number_theory_of_bag, real_bags,
 *   truck_no, package_to_truck, shipments_weight, bags_on_truck, gross_weight,
 *   net_real_weight, note, bag_at_buyer, net_weight_at_buyer, mc_at_buyer,
 *   bb_at_buyer, fm_at_buyer, mouldy_at_buyer, white_bean_at_buyer,
 *   over_s13_at_buyer, deduction_at_buyer.
 *
 * Required (Access `strCheck = ",gdn_id,stack_id,do_no,date_out,"`):
 *   gdn_id, stack_id, do_no, date_out.
 *
 * Combos (server-sourced — Access Form_Load RowSource, NOT free-solo):
 *   do_no            ← combos.do_no  (Autocomplete flat "<CODE> — <NAME>")
 *   lot_id           ← combos.lot_id (label = lot_name)
 *   stack_id         ← combos.stack_id (label = warehouse); refetch filtered
 *                      list khi lot_id đổi (Access `lot_id_AfterUpdate`).
 *   package /
 *   package_to_truck ← static enum BB;PP;JB;BULK (Access Value List).
 *
 * AfterUpdate cascades:
 *   do_no  → delivery-lookups → set to_warehouse + donet; nếu is_move===true
 *            (DO prefix "MOV") → LOCK to_warehouse + donet (Access
 *            `do_no_AfterUpdate`).
 *   lot_id → refetch stack_id combo filtered.
 *
 * Readonly display (KHÔNG trong payload): donet, do_quality, stack_quality
 * (đến từ delivery-lookups).
 *
 * 3 buyer fields white_bean_at_buyer / over_s13_at_buyer / deduction_at_buyer
 * có trong save payload nhưng KHÔNG có control trên Access form (chỉ persist) →
 * giữ nguyên từ row khi edit/copy, null khi new, KHÔNG render input (strict).
 *
 * Modes: new = empty + suggest gdn_id + autofocus. edit = hydrate row, gdn_id
 * KHÔNG disable (Access block disable bị comment out). copy = hydrate row,
 * reset gdn_id (+suggest). Access có duplicate-check gdn_id COMMENTED OUT → FE
 * KHÔNG add check/race-guard, chỉ suggest on New.
 */
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  TextField,
  Typography,
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs from 'dayjs'
import { useEffect, useMemo, useRef, useState } from 'react'

import type { FactoryCell, FactoryRowPayload } from '@/store/api/factoryApi'
import {
  useGetDeliveryCombosQuery,
  useLazyGetDeliveryLookupsQuery,
  useLazyGetSuggestGdnIdQuery,
} from '@/store/api/factoryApi'
import { FORM_GRID_2COL, HIDE_ON_MOBILE, HIDE_ON_MOBILE_INLINE, TEXT_TRUNCATE } from '@/theme/responsive'

type DeliveryRow = { id: string | number } & Record<string, FactoryCell>

interface DraftState {
  gdn_id: string
  date_out: string
  do_no: string
  from_warehouse: string
  to_warehouse: string
  lot_id: string
  lot_name_buyer: string
  stack_id: string
  package: string
  number_theory_of_bag: string
  real_bags: string
  truck_no: string
  package_to_truck: string
  shipments_weight: string
  bags_on_truck: string
  gross_weight: string
  net_real_weight: string
  note: string
  bag_at_buyer: string
  net_weight_at_buyer: string
  mc_at_buyer: string
  bb_at_buyer: string
  fm_at_buyer: string
  mouldy_at_buyer: string
}

const EMPTY_DRAFT: DraftState = {
  gdn_id: '',
  date_out: '',
  do_no: '',
  from_warehouse: '',
  to_warehouse: '',
  lot_id: '',
  lot_name_buyer: '',
  stack_id: '',
  package: '',
  number_theory_of_bag: '',
  real_bags: '',
  truck_no: '',
  package_to_truck: '',
  shipments_weight: '',
  bags_on_truck: '',
  gross_weight: '',
  net_real_weight: '',
  note: '',
  bag_at_buyer: '',
  net_weight_at_buyer: '',
  mc_at_buyer: '',
  bb_at_buyer: '',
  fm_at_buyer: '',
  mouldy_at_buyer: '',
}

/** package / package_to_truck — Access `RowSource = "BB;PP;JB;BULK"`. */
const PACKAGE_ENUM = ['BB', 'PP', 'JB', 'BULK']

const titleBtnSx = {
  py: 0.25,
  px: 1,
  minHeight: 28,
  fontSize: '0.75rem',
  lineHeight: 1.2,
  textTransform: 'none' as const,
}

const compactInputSx = {
  '& .MuiInputBase-root': { minHeight: 32, fontSize: '0.8125rem' },
  '& .MuiInputBase-input': { py: 0.5, fontSize: '0.8125rem' },
  '& .MuiInputLabel-root': { fontSize: '0.8125rem', fontWeight: 500 },
  '& .MuiInputLabel-shrink': { fontWeight: 600 },
}

// Readonly bg standard (skill /convert-form): neutral gray.
const readonlyFieldSx = {
  '& .MuiInputBase-root': {
    bgcolor: (theme: { palette: { mode: string } }) =>
      theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : '#f1f3f5',
  },
  '& .MuiInputBase-input': {
    color: 'text.secondary',
    fontWeight: 600,
  },
}

// Access Format Standard, DecimalPlaces=2 → right-aligned 2 decimals.
const numericInputProps = {
  style: { textAlign: 'right' as const },
  inputMode: 'decimal' as const,
}

/**
 * Standard-format fields — Access `Format="#,#00;(#,#00);;0"` DecimalPlaces=2 →
 * hiển thị `1,234.56` (thousand sep + 2 decimals), reformat on blur, pre-format
 * khi hydrate Edit/Copy (skill /convert-form §Number field). Các field đếm bao
 * (number_theory_of_bag / real_bags / bags_on_truck / bag_at_buyer) là General
 * (no Format) → KHÔNG áp Standard, giữ raw.
 */
const STANDARD_NUM_FIELDS: ReadonlySet<keyof DraftState> = new Set([
  'shipments_weight', 'gross_weight', 'net_real_weight',
  'net_weight_at_buyer', 'mc_at_buyer', 'bb_at_buyer', 'fm_at_buyer', 'mouldy_at_buyer',
])

/** Parse "1,234.5" → 1234.5 ; "" → null (strip thousand separators). */
function parseNumeric(v: FactoryCell | string | null | undefined): number | null {
  const s = (v ?? '').toString().replace(/,/g, '').trim()
  if (!s) return null
  const n = parseFloat(s)
  return Number.isFinite(n) ? n : null
}

/** Format raw → "1,234.56" (Standard, 2 decimals); blank stays blank. */
function formatStandard(v: FactoryCell | string | null | undefined): string {
  const n = parseNumeric(v)
  if (n == null) return ''
  return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

/** Build flat Autocomplete option label "<CODE> — <NAME>"; collapse khi
 *  code === label (memory: combobox bỏ code trùng label). */
function comboLabel(value: string, label: string | null | undefined): string {
  const l = (label ?? '').trim()
  if (!l || l === value) return value
  return `${value} — ${l}`
}

interface ComboItem {
  value: string
  label: string
}

interface DeliveryFormDialogProps {
  open: boolean
  /** 'new' = empty + suggest gdn_id; 'edit' = hydrate row (gdn_id editable —
   *  Access disable block commented out); 'copy' = hydrate row, reset gdn_id. */
  mode: 'new' | 'edit' | 'copy'
  row: DeliveryRow | null
  saving: boolean
  /** Toàn bộ rows hiện có — KHÔNG dùng cho combo (server-sourced); giữ prop để
   *  đồng nhất interface với các form khác (parent wiring). */
  existingRows: DeliveryRow[]
  factoryCode: string
  onClose: () => void
  onSave: (payload: FactoryRowPayload) => void
}

export function DeliveryFormDialog({
  open,
  mode,
  row,
  saving,
  existingRows,
  factoryCode,
  onClose,
  onSave,
}: DeliveryFormDialogProps) {
  const [draft, setDraft] = useState<DraftState>(EMPTY_DRAFT)
  // Readonly display (delivery-lookups). donet/do_quality/stack_quality KHÔNG
  // trong payload. is_move=true → LOCK to_warehouse + donet.
  const [donet, setDonet] = useState<number | null>(null)
  const [doQuality, setDoQuality] = useState('')
  const [stackQuality, setStackQuality] = useState('')
  const [toWarehouseLocked, setToWarehouseLocked] = useState(false)
  // Stack quality gốc từ row (Edit/Copy): readonly field này có sẵn trên
  // view_factory_delivery1 → seed thẳng để Copy hiện ngay; nếu stack_id chưa
  // đổi mà lookup không resolve được (stack cũ) thì fallback về giá trị gốc
  // thay vì để trống.
  const origStackRef = useRef<{ stackId: string; stackQuality: string }>({
    stackId: '',
    stackQuality: '',
  })
  // do_no/stack_id của lần chạy lookup trước — phân biệt user clear (cần xoá
  // readonly) vs mount/hydrate tạm rỗng (giữ seed, đừng wipe).
  const prevLookupKeysRef = useRef<{ doNo: string; stackId: string }>({ doNo: '', stackId: '' })
  // 3 buyer fields chỉ persist (no control). Giữ raw từ row khi edit/copy.
  const [hiddenBuyer, setHiddenBuyer] = useState<{
    white_bean_at_buyer: FactoryCell
    over_s13_at_buyer: FactoryCell
    deduction_at_buyer: FactoryCell
  }>({ white_bean_at_buyer: null, over_s13_at_buyer: null, deduction_at_buyer: null })

  void existingRows

  const [triggerSuggest, suggestState] = useLazyGetSuggestGdnIdQuery()
  const [triggerLookups, { isFetching: lookupsLoading }] = useLazyGetDeliveryLookupsQuery()

  // Combos: do_no + lot_id (unfiltered) + stack_id. Khi lot_id chọn → refetch
  // với lot_id param để lấy stack list filtered (Access lot_id_AfterUpdate).
  const combosQuery = useGetDeliveryCombosQuery(
    { factoryCode, lotId: draft.lot_id.trim() || undefined },
    { skip: !open },
  )
  const doOptions = combosQuery.data?.do_no ?? []
  const lotOptions = combosQuery.data?.lot_id ?? []
  const stackOptions = combosQuery.data?.stack_id ?? []

  /** Hydrate draft — deps `[open, mode, row]` only (memory: hydrate deps). */
  useEffect(() => {
    if (!open) return
    if ((mode === 'edit' || mode === 'copy') && row) {
      setDraft({
        gdn_id: mode === 'copy' ? '' : String(row.gdn_id ?? ''),
        date_out: String(row.date_out ?? '').slice(0, 10),
        do_no: String(row.do_no ?? ''),
        from_warehouse: String(row.from_warehouse ?? ''),
        to_warehouse: String(row.to_warehouse ?? ''),
        lot_id: String(row.lot_id ?? ''),
        lot_name_buyer: String(row.lot_name_buyer ?? ''),
        stack_id: String(row.stack_id ?? ''),
        package: String(row.package ?? ''),
        number_theory_of_bag:
          row.number_theory_of_bag != null ? String(row.number_theory_of_bag) : '',
        real_bags: row.real_bags != null ? String(row.real_bags) : '',
        truck_no: String(row.truck_no ?? ''),
        package_to_truck: String(row.package_to_truck ?? ''),
        shipments_weight: formatStandard(row.shipments_weight),
        bags_on_truck: row.bags_on_truck != null ? String(row.bags_on_truck) : '',
        gross_weight: formatStandard(row.gross_weight),
        net_real_weight: formatStandard(row.net_real_weight),
        note: String(row.note ?? ''),
        bag_at_buyer: row.bag_at_buyer != null ? String(row.bag_at_buyer) : '',
        net_weight_at_buyer: formatStandard(row.net_weight_at_buyer),
        mc_at_buyer: formatStandard(row.mc_at_buyer),
        bb_at_buyer: formatStandard(row.bb_at_buyer),
        fm_at_buyer: formatStandard(row.fm_at_buyer),
        mouldy_at_buyer: formatStandard(row.mouldy_at_buyer),
      })
      setHiddenBuyer({
        white_bean_at_buyer: row.white_bean_at_buyer ?? null,
        over_s13_at_buyer: row.over_s13_at_buyer ?? null,
        deduction_at_buyer: row.deduction_at_buyer ?? null,
      })
      // Readonly field có sẵn trên row → seed ngay (Copy/Edit load Field
      // Disabled, không chờ lookup). stack_quality nằm trên
      // view_factory_delivery1; donet/do_quality không có cột row → lookup điền.
      const seedStackQ = String(row.stack_quality ?? '')
      origStackRef.current = { stackId: String(row.stack_id ?? ''), stackQuality: seedStackQ }
      setStackQuality(seedStackQ)
      setDonet(null)
      setDoQuality('')
      setToWarehouseLocked(false)
      // Copy/Edit có do_no/stack_id → GỌI API delivery-lookups NGAY để lấy
      // DO Net + DO Quality (Access `do_no_AfterUpdate` chạy khi form load
      // record). prevLookupKeysRef set sẵn để effect reactive không fetch trùng.
      const dn0 = String(row.do_no ?? '').trim()
      const sid0 = String(row.stack_id ?? '').trim()
      prevLookupKeysRef.current = { doNo: dn0, stackId: sid0 }
      if (dn0 || sid0) {
        void triggerLookups({ factoryCode, doNo: dn0 || null, stackId: sid0 || null })
          .unwrap()
          .then((res) => {
            setDonet(res.donet)
            setDoQuality(res.do_quality ?? '')
            const fbStackQ =
              sid0 === origStackRef.current.stackId ? origStackRef.current.stackQuality : ''
            setStackQuality(res.stack_quality ?? fbStackQ)
            setToWarehouseLocked(res.is_move === true)
            if (res.to_warehouse != null) {
              setDraft((p) => ({ ...p, to_warehouse: res.to_warehouse ?? '' }))
            }
          })
          .catch(() => undefined)
      }
    } else {
      setDraft({
        ...EMPTY_DRAFT,
        date_out: new Date().toISOString().slice(0, 10),
      })
      setHiddenBuyer({
        white_bean_at_buyer: null,
        over_s13_at_buyer: null,
        deduction_at_buyer: null,
      })
      origStackRef.current = { stackId: '', stackQuality: '' }
      setDonet(null)
      setDoQuality('')
      setStackQuality('')
      prevLookupKeysRef.current = { doNo: '', stackId: '' }
      setToWarehouseLocked(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode, row])

  /** Auto-suggest gdn_id cho new/copy — Access `Suggest_IndexName(... &
   *  "delivery")`. Apply 1 lần khi suggestion arrive và field còn rỗng. */
  useEffect(() => {
    if (!open || mode === 'edit') return
    void triggerSuggest({ factoryCode })
  }, [open, mode, factoryCode, triggerSuggest])

  useEffect(() => {
    const suggested = suggestState.data?.gdn_id
    if (!suggested || mode === 'edit') return
    setDraft((prev) => (prev.gdn_id.trim().length ? prev : { ...prev, gdn_id: suggested }))
  }, [suggestState.data, mode])

  /** from_warehouse default (New only) — Access `Form_Load`
   *  `from_warehouse.DefaultValue = DLookup("receive_warehouse",
   *  "factory_overview", ...)`. Field readonly nên prefill từ combos khi rỗng;
   *  edit/copy giữ giá trị từ row (skip vì mode !== 'new'). */
  useEffect(() => {
    if (!open || mode !== 'new') return
    const rw = combosQuery.data?.receive_warehouse ?? ''
    if (!rw) return
    setDraft((prev) =>
      prev.from_warehouse.trim().length ? prev : { ...prev, from_warehouse: rw },
    )
  }, [open, mode, combosQuery.data])

  /** do_no + stack_id → delivery-lookups (Access `do_no_AfterUpdate`):
   *  set to_warehouse + donet; is_move → lock. do_quality/stack_quality
   *  readonly. */
  useEffect(() => {
    if (!open) return
    const dn = draft.do_no.trim()
    const sid = draft.stack_id.trim()
    if (!dn && !sid) {
      // Chỉ xoá readonly khi user thực sự clear (lần trước có do_no/stack_id).
      // Mount/hydrate tạm rỗng (prev cũng rỗng) → giữ seed từ row, đừng wipe.
      const hadKeys =
        prevLookupKeysRef.current.doNo !== '' || prevLookupKeysRef.current.stackId !== ''
      prevLookupKeysRef.current = { doNo: '', stackId: '' }
      if (hadKeys) {
        setDonet(null)
        setDoQuality('')
        setStackQuality('')
        setToWarehouseLocked(false)
      }
      return
    }
    // Đã fetch cho cặp keys này (vd hydrate Edit/Copy vừa gọi lookup) → skip,
    // tránh gọi API trùng. User đổi do_no/stack_id → keys khác → fetch lại.
    if (dn === prevLookupKeysRef.current.doNo && sid === prevLookupKeysRef.current.stackId) {
      return
    }
    prevLookupKeysRef.current = { doNo: dn, stackId: sid }
    let cancelled = false
    void (async () => {
      try {
        const res = await triggerLookups({
          factoryCode,
          doNo: dn || null,
          stackId: sid || null,
        }).unwrap()
        if (cancelled) return
        setDonet(res.donet)
        setDoQuality(res.do_quality ?? '')
        // Lookup không resolve mà stack_id vẫn là stack gốc → giữ giá trị row
        // (Field Disabled không bị trống khi Copy/Edit). Stack đổi → theo lookup.
        const fallbackStackQ =
          sid === origStackRef.current.stackId ? origStackRef.current.stackQuality : ''
        setStackQuality(res.stack_quality ?? fallbackStackQ)
        setToWarehouseLocked(res.is_move === true)
        // Access set to_warehouse từ lookup (cả move + delivery order).
        if (res.to_warehouse != null) {
          setDraft((prev) => ({ ...prev, to_warehouse: res.to_warehouse ?? '' }))
        }
      } catch {
        if (cancelled) return
      }
    })()
    return () => {
      cancelled = true
    }
  }, [open, draft.do_no, draft.stack_id, factoryCode, triggerLookups])

  const selectedDo = useMemo<ComboItem | null>(() => {
    const v = draft.do_no.trim()
    if (!v) return null
    const found = doOptions.find((o) => o.value === v)
    return found ? { value: found.value, label: found.label } : { value: v, label: v }
  }, [draft.do_no, doOptions])

  const selectedLot = useMemo<ComboItem | null>(() => {
    const v = draft.lot_id.trim()
    if (!v) return null
    const found = lotOptions.find((o) => o.value === v)
    return found ? { value: found.value, label: found.label } : { value: v, label: v }
  }, [draft.lot_id, lotOptions])

  const selectedStack = useMemo<ComboItem | null>(() => {
    const v = draft.stack_id.trim()
    if (!v) return null
    const found = stackOptions.find((o) => o.value === v)
    return found ? { value: found.value, label: found.label } : { value: v, label: v }
  }, [draft.stack_id, stackOptions])

  // Inject fallback `sel` vào CHÍNH options khi vắng → tắt MUI warning "value
  // invalid / none of options match" (skill /convert-form anti-pattern #46).
  const doItems = useMemo<ComboItem[]>(() => {
    const base = doOptions.map((o) => ({ value: o.value, label: o.label }))
    return selectedDo && !base.some((o) => o.value === selectedDo.value) ? [selectedDo, ...base] : base
  }, [doOptions, selectedDo])
  const lotItems = useMemo<ComboItem[]>(() => {
    const base = lotOptions.map((o) => ({ value: o.value, label: o.label }))
    return selectedLot && !base.some((o) => o.value === selectedLot.value) ? [selectedLot, ...base] : base
  }, [lotOptions, selectedLot])
  const stackItems = useMemo<ComboItem[]>(() => {
    const base = stackOptions.map((o) => ({ value: o.value, label: o.label }))
    return selectedStack && !base.some((o) => o.value === selectedStack.value) ? [selectedStack, ...base] : base
  }, [stackOptions, selectedStack])

  const gdnIdEmpty = draft.gdn_id.trim().length === 0
  const stackIdEmpty = draft.stack_id.trim().length === 0
  const doNoEmpty = draft.do_no.trim().length === 0
  const dateOutEmpty = draft.date_out.trim().length === 0
  const canSave =
    !gdnIdEmpty && !stackIdEmpty && !doNoEmpty && !dateOutEmpty && !saving

  const handleChange = <K extends keyof DraftState>(key: K, value: string) =>
    setDraft((prev) => ({ ...prev, [key]: value }))

  /** Reformat Standard numeric field → "1,234.56" on blur (skill §Number). */
  const handleStandardBlur = (key: keyof DraftState) => () =>
    setDraft((prev) => {
      if (!STANDARD_NUM_FIELDS.has(key)) return prev
      const f = formatStandard(prev[key])
      return f === prev[key] ? prev : { ...prev, [key]: f }
    })

  /** lot_id đổi → clear stack_id (RowSource thay đổi nên stack cũ có thể
   *  không còn hợp lệ) + set lot_name_buyer từ combo label nếu có. */
  const handleLotChange = (value: string) => {
    const found = lotOptions.find((o) => o.value === value)
    setDraft((prev) =>
      prev.lot_id === value
        ? prev
        : {
            ...prev,
            lot_id: value,
            stack_id: '',
            lot_name_buyer: found?.label ?? prev.lot_name_buyer,
          },
    )
  }

  const buildPayload = (): FactoryRowPayload => {
    const str = (v: string): string | null => {
      const s = v.trim()
      return s.length ? s : null
    }
    const num = (v: string): number | null => {
      if (!v.trim()) return null
      const n = parseFloat(v.replace(/,/g, ''))
      return Number.isFinite(n) ? n : null
    }
    // Exact order theo Access strU_mainsub.
    return {
      gdn_id: draft.gdn_id.trim(),
      date_out: str(draft.date_out),
      do_no: draft.do_no.trim(),
      from_warehouse: str(draft.from_warehouse),
      to_warehouse: str(draft.to_warehouse),
      lot_id: str(draft.lot_id),
      lot_name_buyer: str(draft.lot_name_buyer),
      stack_id: draft.stack_id.trim(),
      package: str(draft.package),
      number_theory_of_bag: num(draft.number_theory_of_bag),
      real_bags: num(draft.real_bags),
      truck_no: str(draft.truck_no),
      package_to_truck: str(draft.package_to_truck),
      shipments_weight: num(draft.shipments_weight),
      bags_on_truck: num(draft.bags_on_truck),
      gross_weight: num(draft.gross_weight),
      net_real_weight: num(draft.net_real_weight),
      note: str(draft.note),
      bag_at_buyer: num(draft.bag_at_buyer),
      net_weight_at_buyer: num(draft.net_weight_at_buyer),
      mc_at_buyer: num(draft.mc_at_buyer),
      bb_at_buyer: num(draft.bb_at_buyer),
      fm_at_buyer: num(draft.fm_at_buyer),
      mouldy_at_buyer: num(draft.mouldy_at_buyer),
      white_bean_at_buyer: hiddenBuyer.white_bean_at_buyer ?? null,
      over_s13_at_buyer: hiddenBuyer.over_s13_at_buyer ?? null,
      deduction_at_buyer: hiddenBuyer.deduction_at_buyer ?? null,
    }
  }

  const handleSave = () => {
    if (!canSave) return
    onSave(buildPayload())
  }

  const donetDisplay =
    donet == null
      ? ''
      : donet.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

  return (
    <Dialog
      open={open}
      onClose={(_event, reason) => {
        if (saving) return
        if (reason === 'backdropClick') return
        onClose()
      }}
      maxWidth="lg"
      fullWidth
      scroll="paper"
      PaperProps={{
        sx: {
          maxHeight: 'min(880px, 94vh)',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <DialogTitle
        sx={{
          pb: 1,
          pt: 1.25,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flexShrink: 0,
        }}
      >
        <Typography
          component="span"
          noWrap
          title={`${mode === 'new' ? 'New' : mode === 'copy' ? 'Copy' : 'Edit'} — Factory · Delivery`}
          sx={{ ...TEXT_TRUNCATE, flex: '1 1 auto', fontWeight: 700, fontSize: '1rem', minWidth: 0 }}
        >
          {mode === 'new' ? 'New' : mode === 'copy' ? 'Copy' : 'Edit'} —
          <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>
            {' Factory ·'}
          </Box>
          {' Delivery'}
        </Typography>
        <Typography component="span" variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
          {factoryCode.toUpperCase()}
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', ml: 'auto', flexShrink: 0 }}>
          <Button
            type="button"
            variant="contained"
            size="small"
            color="success"
            onClick={handleSave}
            disabled={!canSave}
            sx={titleBtnSx}
          >
            {saving ? <CircularProgress size={14} color="inherit" /> : 'Save'}
          </Button>
          <Button
            type="button"
            variant="outlined"
            size="small"
            onClick={onClose}
            disabled={saving}
            sx={titleBtnSx}
          >
            Cancel
          </Button>
        </Box>
      </DialogTitle>
      <Divider sx={{ flexShrink: 0 }} />
      <DialogContent
        dividers
        sx={{
          ...compactInputSx,
          overflowY: 'auto',
          flex: '1 1 auto',
          minHeight: 0,
          pt: 1.5,
        }}
      >
        {/* 2-col grid bám Access Left=3420 (trái) / Left=9180 (phải), rows theo
            Top. gridRow/gridColumn explicit per field (memory: form grid align). */}
        <Box sx={FORM_GRID_2COL}>
          {/* Row 1 (T=120) — gdn_id * | date_out * */}
          <TextField
            sx={{ gridColumn: { xs: 1, sm: 1 }, gridRow: { sm: 1 } }}
            label="GDN ID"
            size="small"
            fullWidth
            required
            autoFocus={mode === 'new' || mode === 'copy'}
            value={draft.gdn_id}
            onChange={(e) => handleChange('gdn_id', e.target.value)}
            helperText={
              mode === 'edit' ? undefined : (
                <Box component="span" sx={HIDE_ON_MOBILE}>
                  {suggestState.isFetching
                    ? 'Suggesting next code…'
                    : mode === 'copy'
                      ? `Suggested code; cloned from ${String(row?.gdn_id ?? '')}.`
                      : 'Suggested code below; edit if needed.'}
                </Box>
              )
            }
          />
          <DatePicker
            label="Date Out"
            format="DD-MMM-YYYY"
            value={draft.date_out ? dayjs(draft.date_out) : null}
            onChange={(d) =>
              handleChange('date_out', d && d.isValid() ? d.format('YYYY-MM-DD') : '')
            }
            slotProps={{
              textField: {
                size: 'small',
                fullWidth: true,
                required: true,
                InputLabelProps: { shrink: true },
                sx: { gridColumn: { xs: 1, sm: 2 }, gridRow: { sm: 1 } },
              },
            }}
          />

          {/* Row 2 (T=504) — do_no * | (right empty until donet) */}
          <Autocomplete<ComboItem, false, false, false>
            sx={{ gridColumn: { xs: 1, sm: 1 }, gridRow: { sm: 2 } }}
            size="small"
            options={doItems}
            value={selectedDo}
            loading={combosQuery.isFetching}
            getOptionLabel={(o) => comboLabel(o.value, o.label)}
            isOptionEqualToValue={(o, v) => o.value === v.value}
            onChange={(_, v) => handleChange('do_no', v?.value ?? '')}
            renderInput={(params) => (
              <TextField {...params} label="DO No" required fullWidth />
            )}
          />
          <TextField
            sx={{ gridColumn: { xs: 1, sm: 2 }, gridRow: { sm: 2 }, ...readonlyFieldSx }}
            label="DO Net"
            size="small"
            fullWidth
            value={donetDisplay}
            InputProps={{
              readOnly: true,
              endAdornment: lookupsLoading ? <CircularProgress size={14} /> : null,
            }}
            InputLabelProps={{ shrink: true }}
            inputProps={numericInputProps}
          />

          {/* Row 3 (T=888) — do_quality (RO, left col only; right empty per Access) */}
          <TextField
            sx={{ gridColumn: { xs: 1, sm: 1 }, gridRow: { sm: 3 }, ...readonlyFieldSx }}
            label="DO Quality"
            size="small"
            fullWidth
            value={doQuality}
            InputProps={{ readOnly: true }}
            InputLabelProps={{ shrink: true }}
          />

          {/* Row 4 (T=1272) — from_warehouse (RO, Access Enabled=NotDefault) | to_warehouse */}
          <TextField
            sx={{ gridColumn: { xs: 1, sm: 1 }, gridRow: { sm: 4 }, ...readonlyFieldSx }}
            label="From Warehouse"
            size="small"
            fullWidth
            value={draft.from_warehouse}
            InputProps={{ readOnly: true }}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            sx={{ gridColumn: { xs: 1, sm: 2 }, gridRow: { sm: 4 } }}
            label="To Warehouse"
            size="small"
            fullWidth
            disabled={toWarehouseLocked}
            value={draft.to_warehouse}
            onChange={(e) => handleChange('to_warehouse', e.target.value)}
            helperText={
              toWarehouseLocked ? (
                <Box component="span" sx={HIDE_ON_MOBILE}>
                  Set automatically from the moving order.
                </Box>
              ) : undefined
            }
            InputLabelProps={{ shrink: true }}
          />

          {/* Row 5 (T=1656) — lot_id | lot_name_buyer */}
          <Autocomplete<ComboItem, false, false, false>
            sx={{ gridColumn: { xs: 1, sm: 1 }, gridRow: { sm: 5 } }}
            size="small"
            options={lotItems}
            value={selectedLot}
            loading={combosQuery.isFetching}
            getOptionLabel={(o) => comboLabel(o.value, o.label)}
            isOptionEqualToValue={(o, v) => o.value === v.value}
            onChange={(_, v) => handleLotChange(v?.value ?? '')}
            renderInput={(params) => <TextField {...params} label="Lot ID" fullWidth />}
          />
          <TextField
            sx={{ gridColumn: { xs: 1, sm: 2 }, gridRow: { sm: 5 } }}
            label="Lot Name Buyer"
            size="small"
            fullWidth
            value={draft.lot_name_buyer}
            onChange={(e) => handleChange('lot_name_buyer', e.target.value)}
          />

          {/* Row 6 (T=2040) — stack_id * | package */}
          <Autocomplete<ComboItem, false, false, false>
            sx={{ gridColumn: { xs: 1, sm: 1 }, gridRow: { sm: 6 } }}
            size="small"
            options={stackItems}
            value={selectedStack}
            loading={combosQuery.isFetching}
            getOptionLabel={(o) => comboLabel(o.value, o.label)}
            isOptionEqualToValue={(o, v) => o.value === v.value}
            onChange={(_, v) => handleChange('stack_id', v?.value ?? '')}
            renderInput={(params) => (
              <TextField {...params} label="Stack ID" required fullWidth />
            )}
          />
          <Autocomplete<string, false, false, false>
            sx={{ gridColumn: { xs: 1, sm: 2 }, gridRow: { sm: 6 } }}
            size="small"
            options={PACKAGE_ENUM}
            value={draft.package || null}
            onChange={(_, v) => handleChange('package', v ?? '')}
            renderInput={(params) => <TextField {...params} label="Package" fullWidth />}
          />

          {/* Row 7 (T=2424) — stack_quality (RO, left col only; right empty per Access) */}
          <TextField
            sx={{ gridColumn: { xs: 1, sm: 1 }, gridRow: { sm: 7 }, ...readonlyFieldSx }}
            label="Stack Quality"
            size="small"
            fullWidth
            value={stackQuality}
            InputProps={{ readOnly: true }}
            InputLabelProps={{ shrink: true }}
          />

          {/* Row 8 (T=2808) — number_theory_of_bag | real_bags */}
          <TextField
            sx={{ gridColumn: { xs: 1, sm: 1 }, gridRow: { sm: 8 } }}
            label="Number Theory Of Bag"
            size="small"
            fullWidth
            type="text"
            value={draft.number_theory_of_bag}
            onChange={(e) => handleChange('number_theory_of_bag', e.target.value)}
            inputProps={numericInputProps}
          />
          <TextField
            sx={{ gridColumn: { xs: 1, sm: 2 }, gridRow: { sm: 8 } }}
            label="Real Bags"
            size="small"
            fullWidth
            type="text"
            value={draft.real_bags}
            onChange={(e) => handleChange('real_bags', e.target.value)}
            inputProps={numericInputProps}
          />

          {/* Row 9 (T=3192) — truck_no | package_to_truck */}
          <TextField
            sx={{ gridColumn: { xs: 1, sm: 1 }, gridRow: { sm: 9 } }}
            label="Truck No"
            size="small"
            fullWidth
            value={draft.truck_no}
            onChange={(e) => handleChange('truck_no', e.target.value)}
          />
          <Autocomplete<string, false, false, false>
            sx={{ gridColumn: { xs: 1, sm: 2 }, gridRow: { sm: 9 } }}
            size="small"
            options={PACKAGE_ENUM}
            value={draft.package_to_truck || null}
            onChange={(_, v) => handleChange('package_to_truck', v ?? '')}
            renderInput={(params) => <TextField {...params} label="Package To Truck" fullWidth />}
          />

          {/* Row 10 (T=3576) — shipments_weight | bags_on_truck */}
          <TextField
            sx={{ gridColumn: { xs: 1, sm: 1 }, gridRow: { sm: 10 } }}
            label="Shipments Weight"
            size="small"
            fullWidth
            type="text"
            value={draft.shipments_weight}
            onChange={(e) => handleChange('shipments_weight', e.target.value)}
            onBlur={handleStandardBlur('shipments_weight')}
            inputProps={numericInputProps}
          />
          <TextField
            sx={{ gridColumn: { xs: 1, sm: 2 }, gridRow: { sm: 10 } }}
            label="Bags On Truck"
            size="small"
            fullWidth
            type="text"
            value={draft.bags_on_truck}
            onChange={(e) => handleChange('bags_on_truck', e.target.value)}
            inputProps={numericInputProps}
          />

          {/* Row 11 (T=3960) — gross_weight | net_real_weight */}
          <TextField
            sx={{ gridColumn: { xs: 1, sm: 1 }, gridRow: { sm: 11 } }}
            label="Gross Weight"
            size="small"
            fullWidth
            type="text"
            value={draft.gross_weight}
            onChange={(e) => handleChange('gross_weight', e.target.value)}
            onBlur={handleStandardBlur('gross_weight')}
            inputProps={numericInputProps}
          />
          <TextField
            sx={{ gridColumn: { xs: 1, sm: 2 }, gridRow: { sm: 11 } }}
            label="Net Real Weight"
            size="small"
            fullWidth
            type="text"
            value={draft.net_real_weight}
            onChange={(e) => handleChange('net_real_weight', e.target.value)}
            onBlur={handleStandardBlur('net_real_weight')}
            inputProps={numericInputProps}
          />

          {/* Row 12 (T=4344) — note (full width, span 2 cols) */}
          <TextField
            sx={{ gridColumn: { xs: 1, sm: '1 / span 2' }, gridRow: { sm: 12 } }}
            label="Note"
            size="small"
            fullWidth
            multiline
            rows={2}
            value={draft.note}
            onChange={(e) => handleChange('note', e.target.value)}
          />

          {/* Section header "Receive at Buyer" — Access Label (L=612 T=4752),
              chia block buyer khỏi phần trên. */}
          <Box
            sx={{
              gridColumn: { xs: 1, sm: '1 / span 2' },
              gridRow: { sm: 13 },
              mt: 0.5,
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 700,
                color: 'text.secondary',
                borderBottom: 1,
                borderColor: 'divider',
                pb: 0.25,
              }}
            >
              Receive at Buyer
            </Typography>
          </Box>

          {/* Buyer block (T=5136 →). Left: bag_at_buyer / mc_at_buyer /
              fm_at_buyer. Right: net_weight_at_buyer / bb_at_buyer /
              mouldy_at_buyer. */}
          {/* Row 14 (T=5136) — bag_at_buyer | net_weight_at_buyer */}
          <TextField
            sx={{ gridColumn: { xs: 1, sm: 1 }, gridRow: { sm: 14 } }}
            label="Bag At Buyer"
            size="small"
            fullWidth
            type="text"
            value={draft.bag_at_buyer}
            onChange={(e) => handleChange('bag_at_buyer', e.target.value)}
            inputProps={numericInputProps}
          />
          <TextField
            sx={{ gridColumn: { xs: 1, sm: 2 }, gridRow: { sm: 14 } }}
            label="Net Weight At Buyer"
            size="small"
            fullWidth
            type="text"
            value={draft.net_weight_at_buyer}
            onChange={(e) => handleChange('net_weight_at_buyer', e.target.value)}
            onBlur={handleStandardBlur('net_weight_at_buyer')}
            inputProps={numericInputProps}
          />

          {/* Row 15 (T=5520) — mc_at_buyer | bb_at_buyer */}
          <TextField
            sx={{ gridColumn: { xs: 1, sm: 1 }, gridRow: { sm: 15 } }}
            label="MC At Buyer"
            size="small"
            fullWidth
            type="text"
            value={draft.mc_at_buyer}
            onChange={(e) => handleChange('mc_at_buyer', e.target.value)}
            onBlur={handleStandardBlur('mc_at_buyer')}
            inputProps={numericInputProps}
          />
          <TextField
            sx={{ gridColumn: { xs: 1, sm: 2 }, gridRow: { sm: 15 } }}
            label="BB At Buyer"
            size="small"
            fullWidth
            type="text"
            value={draft.bb_at_buyer}
            onChange={(e) => handleChange('bb_at_buyer', e.target.value)}
            onBlur={handleStandardBlur('bb_at_buyer')}
            inputProps={numericInputProps}
          />

          {/* Row 16 (T=5904) — fm_at_buyer | mouldy_at_buyer */}
          <TextField
            sx={{ gridColumn: { xs: 1, sm: 1 }, gridRow: { sm: 16 } }}
            label="FM At Buyer"
            size="small"
            fullWidth
            type="text"
            value={draft.fm_at_buyer}
            onChange={(e) => handleChange('fm_at_buyer', e.target.value)}
            onBlur={handleStandardBlur('fm_at_buyer')}
            inputProps={numericInputProps}
          />
          <TextField
            sx={{ gridColumn: { xs: 1, sm: 2 }, gridRow: { sm: 16 } }}
            label="Mouldy At Buyer"
            size="small"
            fullWidth
            type="text"
            value={draft.mouldy_at_buyer}
            onChange={(e) => handleChange('mouldy_at_buyer', e.target.value)}
            onBlur={handleStandardBlur('mouldy_at_buyer')}
            inputProps={numericInputProps}
          />
        </Box>
      </DialogContent>
    </Dialog>
  )
}

export default DeliveryFormDialog
